import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { Input, Label, Select } from '../Form';
import { useMutation } from '@apollo/client';
import useNotify from '../../hooks/useNotify';
import FormRow from '../Form/FormRow';
import FormColumn from '../Form/FormColumn';
import Form from '../Form/Form';
import useFileUpload from '../../hooks/useFileUpload';
import StatePicker from '../Address/StatePicker';
import { Credential, CredentialIssuer, CredentialType } from '../Tester/types';
import UploadSelectPreview from '../File/UploadSelectPreview';
import { TempFile } from '../../hooks/useUploadTempFile';
import { useMode } from '../../hooks/useMode';
import ButtonToggle from '../Button/ButtonToggle';
import {
  ISSUER_CAN_HAVE_CITY,
  ISSUER_CAN_HAVE_STATE,
  ISSUER_REQUIRES_CITY,
  ISSUER_REQUIRES_STATE,
  TYPE_CAN_BE_DEFAULT,
} from './rules';
import { issuers, types } from './values';
import { Flex } from 'rebass';
import { gql } from '@/modes/individual/gql';

const CREATE_CREDENTIAL = gql(`
  mutation CreateCredential($input: CredentialInput!) {
    createCredential(input: $input) {
      id
      type
      issuer
      value
      city
      state
      issueDate
      expireDate
      isDefault
      created
      updated
    }
  }
`);

const UPDATE_CREDENTIAL = gql(`
  mutation UpdateCredential($credentialId: ID!, $input: CredentialInput!) {
    updateCredential(credentialId: $credentialId, input: $input) {
      id
      type
      issuer
      value
      city
      state
      issueDate
      expireDate
      isDefault
      created
      updated
    }
  }
`);

const ARCHIVE_CREDENTIAL = gql(`
  mutation ArchiveCredential($credentialId: ID!) {
    archiveCredential(credentialId: $credentialId) {
      id
      type
      issuer
      value
      city
      state
      issueDate
      expireDate
      isDefault
      created
      updated
    }
  }
`);

type CredentialInput = {
  id?: string;
  profileId?: string;
  type: CredentialType;
  issuer: CredentialIssuer;
  city: string | null;
  // county: string
  state: string | null;
  value: string | null;
  issueDate: string | null;
  isDefault?: boolean;
  expireDate: string | null;
};

type CredentialEditModalProps = {
  onClose: () => void;
  onCreate?: (credential: Credential) => void;
  readOnly?: boolean;
} & (
  | {
      action: 'create';
      credential: Omit<Partial<CredentialInput>, 'type'> &
        Pick<CredentialInput, 'type'>;
    }
  | {
      action: 'edit';
      credential: Omit<Credential, 'archived'> & CredentialInput;
    }
);

const CredentialEditModal: React.FC<CredentialEditModalProps> = ({
  onClose,
  credential,
  action,
  onCreate,
  readOnly = false,
}) => {
  const { showError, showSuccess } = useNotify();
  const mode = useMode();

  const typeName =
    types.find((type) => type.value === credential.type)?.label ?? 'Credential';

  const [credentialInput, setCredentialInput] = useState<CredentialInput>({
    profileId: credential.profileId ?? '',
    type: credential.type,
    issuer: credential.issuer ?? 'STATE',
    city: credential.city ?? '',
    state: credential.state ?? '',
    value: credential.value ?? '',
    issueDate: credential.issueDate ?? '',
    expireDate: credential.expireDate ?? '',
    isDefault: !!credential.isDefault,
  });

  const accept = {
    'image/*': ['.jpg', '.jpeg', '.png', '.webp', '.tiff'],
    'application/pdf': ['.pdf'],
  };
  const dropZone = useFileUpload({
    multiple: false,
    accept,
  });
  const { doUpload, loading: uploadLoading, acceptedFile } = dropZone;

  const onInputChange = useCallback((e: any) => {
    const { name } = e.target;
    let { value } = e.target;
    if (['true', 'false'].includes(value)) {
      value = value === 'true';
    }
    setCredentialInput((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  const [createCredential, { loading: createLoading }] = useMutation<
    { createCredential: CredentialInput },
    { input: Omit<CredentialInput, 'id'> & { tempKey: string } }
  >(CREATE_CREDENTIAL, {
    onError: (error) => {
      showError(`Error adding ${typeName}`, error);
      console.error(`Error adding ${typeName}`, error);
    },
  });

  const [updateCredential, { loading: updateLoading }] = useMutation<
    { updateCredential: CredentialInput },
    {
      credentialId: string;
      input: Omit<CredentialInput, 'id'> & { tempKey?: string };
    }
  >(UPDATE_CREDENTIAL, {
    onError: (error) => {
      showError(`Error updating ${typeName}`, error);
      console.error(`Error updating ${typeName}`, error);
    },
  });

  const [archiveCredential, { loading: archiveLoading }] = useMutation<
    { archiveCredential: CredentialInput },
    {
      credentialId: string;
    }
  >(ARCHIVE_CREDENTIAL, {
    variables: {
      credentialId: credential.id!,
    },
    onError: (error) => {
      showError(`Error archiving ${typeName}`, error);
      console.error(`Error archiving ${typeName}`, error);
    },
    onCompleted: () => {
      showSuccess(`${typeName} archived`);
      onClose();
    },
  });

  const archive = useCallback(() => {
    if (confirm(`Are you sure you want to archive this ${typeName}?`)) {
      archiveCredential();
    }
  }, [typeName, archiveCredential]);

  const save = useCallback(async () => {
    if (!acceptedFile && action === 'create') {
      showError(`Please provide an image`);
      return;
    }
    if (!credentialInput.value) {
      showError('Certification number is required');
      return;
    }
    if (!credentialInput.type) {
      showError('Type is required');
      return;
    }
    if (!credentialInput.issuer) {
      showError('Issuer is required');
      return;
    }

    if (mode === 'individual') {
      delete credentialInput.profileId;
    } else if (!credentialInput.profileId) {
      showError('Profile ID is required');
      return;
    }

    if (
      ISSUER_REQUIRES_CITY.includes(credentialInput.issuer) &&
      !credentialInput.city
    ) {
      showError('City is required');
      return;
    }

    if (
      ISSUER_REQUIRES_STATE.includes(credentialInput.issuer) &&
      !credentialInput.state
    ) {
      showError('State is required');
      return;
    }
    if (!credentialInput.issueDate) {
      showError('Issue date is required');
      return;
    }
    if (!credentialInput.expireDate) {
      showError('Expiration date is required');
      return;
    }

    let tempFile: TempFile | undefined;
    if (acceptedFile) {
      try {
        tempFile = (await doUpload())[0];
      } catch (e) {
        showError('Error uploading file', e as Error);
        return;
      }
    }

    const input: CredentialInput = {
      ...credentialInput,
      city: ISSUER_CAN_HAVE_CITY.includes(credentialInput.issuer)
        ? credentialInput.city
        : '',
      state: ISSUER_CAN_HAVE_STATE.includes(credentialInput.issuer)
        ? credentialInput.state
        : '',
      isDefault: TYPE_CAN_BE_DEFAULT.includes(credentialInput.type)
        ? credentialInput.isDefault
        : false,
    };

    switch (action) {
      case 'create':
        if (mode === 'individual') {
          //   delete credentialInput.profileId
          // If we remove the profileId, the credential service will throw, because it is expecting one
          // If we keep the profileId, the mutation will return a 500 error, because
          // the mode === company and mode === individual have divergent schema definitions for a credentialInput
          // one lists it option, the other does not list it
          // This issue is mirrored for the edit flow
        }
        await createCredential({
          variables: {
            input: { ...input, tempKey: tempFile?.key as string },
          },
        })
          .then(async (result) => {
            if (result.data && result.data.createCredential) {
              showSuccess(`${typeName} successfully added`);
              if (onCreate) {
                const newCredential = result.data
                  .createCredential as Credential;
                onCreate(newCredential);
              }
              onClose();
            }
          })
          .catch((error) => {
            showError('Something went wrong', error);
            console.error(error.errors);
          });
        break;
      case 'edit':
        await updateCredential({
          variables: {
            credentialId: credential.id as string,
            input: { ...input, tempKey: tempFile?.key },
          },
        })
          .then(async (result) => {
            if (result.data && result.data.updateCredential) {
              // Do something with data here?
              showSuccess(`${typeName} successfully updated!`);
              onClose();
            }
          })
          .catch((error) => {
            showError('Something went wrong', error);
            console.error(error.errors);
          });
        break;
    }
  }, [
    action,
    credential.id,
    credentialInput,
    createCredential,
    updateCredential,
    acceptedFile,
    doUpload,
  ]);

  const isSaving = createLoading || updateLoading || uploadLoading;

  return (
    <Modal
      title={action === 'edit' ? `Edit ${typeName}` : `Add ${typeName}`}
      onClose={onClose}
    >
      <ModalBody>
        <Form>
          <FormRow>
            <FormColumn>
              <FormColumn>
                <Label>{typeName} Number</Label>
                <Input
                  required
                  name="value"
                  type="text"
                  autoComplete="off"
                  width="100%"
                  value={credentialInput.value ?? ''}
                  autoFocus
                  onChange={onInputChange}
                  disabled={readOnly}
                />
              </FormColumn>
              <FormColumn>
                <Label>Issuer</Label>
                <Select
                  name="issuer"
                  width="100%"
                  value={credentialInput.issuer}
                  onChange={onInputChange}
                  disabled={readOnly}
                >
                  {issuers
                    .filter((issuer) =>
                      issuer.types.includes(credentialInput.type)
                    )
                    .map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                </Select>
              </FormColumn>
              {ISSUER_CAN_HAVE_CITY.includes(credentialInput.issuer) && (
                <FormColumn>
                  <Label>City</Label>
                  <Input
                    required={ISSUER_REQUIRES_CITY.includes(
                      credentialInput.issuer
                    )}
                    name="city"
                    type="text"
                    autoComplete="off"
                    width="100%"
                    value={credentialInput.city ?? ''}
                    onChange={onInputChange}
                    disabled={readOnly}
                  />
                </FormColumn>
              )}
              {/*{credentialInput.type !== 'STATE' && (*/}
              {/*  <FormColumn>*/}
              {/*    <Label>County</Label>*/}
              {/*    <Input*/}
              {/*      name="county"*/}
              {/*      type="text"*/}
              {/*      autoComplete="off"*/}
              {/*      width="100%"*/}
              {/*      value={credentialInput.county}*/}
              {/*      onChange={onInputChange}*/}
              {/*    />*/}
              {/*  </FormColumn>*/}
              {/*)}*/}
              {ISSUER_CAN_HAVE_STATE.includes(credentialInput.issuer) && (
                <FormColumn>
                  <Label>State</Label>
                  <StatePicker
                    required={ISSUER_REQUIRES_STATE.includes(
                      credentialInput.issuer
                    )}
                    name="state"
                    value={credentialInput.state ?? ''}
                    onChange={onInputChange}
                    width="100%"
                    disabled={readOnly}
                  />
                </FormColumn>
              )}
              <FormColumn>
                <Label>Issue Date</Label>
                <Input
                  required
                  name="issueDate"
                  type="date"
                  autoComplete="off"
                  value={credentialInput.issueDate ?? ''}
                  onChange={onInputChange}
                  disabled={readOnly}
                />
              </FormColumn>
              <FormColumn>
                <Label>Expiration Date</Label>
                <Input
                  required
                  name="expireDate"
                  type="date"
                  autoComplete="off"
                  value={credentialInput.expireDate ?? ''}
                  onChange={onInputChange}
                  disabled={readOnly}
                />
              </FormColumn>
              {TYPE_CAN_BE_DEFAULT.includes(credentialInput.type) && (
                <FormColumn>
                  <Label>Is Default</Label>
                  <ButtonToggle
                    name="isDefault"
                    value={credentialInput.isDefault}
                    onClick={onInputChange}
                    disabled={readOnly}
                  />
                </FormColumn>
              )}
            </FormColumn>

            <FormColumn>
              <UploadSelectPreview
                accept={accept}
                existingFile={action === 'edit' ? credential.image : undefined}
                uploadState={dropZone}
                readOnly={readOnly}
              />
            </FormColumn>
          </FormRow>
        </Form>
      </ModalBody>
      <ModalFooter>
        {action === 'edit' && !readOnly && (
          <Button
            alignSelf="flex-start"
            icon="trash"
            variant="danger"
            onClick={archive}
            disabled={archiveLoading}
          >
            Archive
          </Button>
        )}
        <Flex flexGrow={1} justifyContent="flex-end" sx={{ gap: 2 }}>
          {!readOnly && (
            <Button icon="save" onClick={save} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          )}
          <Button
            variant="outline"
            icon="slash-circle"
            onClick={onClose}
            disabled={isSaving}
          >
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};

CredentialEditModal.propTypes = {
  onClose: PropTypes.any,
  credential: PropTypes.any,
  action: PropTypes.any.isRequired,
};

export default CredentialEditModal;
